import React, { ReactNode, useMemo } from "react"
import { Link, useRouteMatch } from "react-router-dom"
import {
    IconDiamond,
    IconEdit,
    IconEye,
    IconOverview,
    IconSettings,
    Title
} from "dash-styled"
import { ReactComponent as IconWelcome } from "icons/lib/hand.svg"
import { ReactComponent as IconUpload } from "icons/lib/upload.svg"
import { ReactComponent as IconGear } from "icons/lib/gear.svg"
import { Map } from "immutable"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { APIJobQueryModel } from "../../../../../../Models/API/JobModel"
import { getValue, hasValue } from "../../../../../../Models/Client/FetchState"
import { routePaths } from "../../../../../../Routes"
import { ReviewUnreviewedButton } from "../Common/ReviewUnreviewedButton"
import { InstanceNameViewEdit } from "./InstanceNameViewEdit"
import { MenuItem } from "./MenuItem"
import {
    AppInstanceStatus,
    isAppInstanceEnabled
} from "../../../../../../Models/API/AppInstanceModel"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { ReactComponent as MessagesIcon } from "icons/lib/messages.svg"
import { useAppDetailsPaths } from "../../../../../../Hooks/useAppDetailsPaths"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { useJobIdToReview } from "../../../../../../Hooks/useJobIdToReview"
import { useJobCount } from "../../../../../../Hooks/useJobCount"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import {
    selectHasFreemiumExpired,
    selectIsSysAdmin
} from "../../../../../../Helpers/Functions/Selectors/auth"

interface MenuProps {
    orgUsername: string
    appID: string
    canLabel: boolean
    couldLabel: boolean
    filterAndSort?: FilterAndSortExpression<APIJobQueryModel>
}

const renderAction = (
    [key, action]: [string, ReactNode],
    index: number,
    arr: ReactNode[]
) => (
    <li
        key={key}
        className={`${
            arr.length === 1 ? "w-full" : arr.length === 2 ? "w-1/2" : "w-1/3"
        } ${
            index < arr.length - 1 ? (arr.length === 3 ? "pr-px" : "pr-1") : ""
        }`}
    >
        {action}
    </li>
)

export function Menu({
    orgUsername,
    appID,
    filterAndSort,
    canLabel,
    couldLabel
}: MenuProps) {
    const workerPathMatch = useRouteMatch(
        routePaths.appWorkerList(orgUsername, appID)
    )
    const workerGroupPathMatch = useRouteMatch(
        routePaths.appWorkerGroupList(orgUsername, appID)
    )
    const qualifierPathMatch = useRouteMatch(
        routePaths.appQualifiers(orgUsername, appID)
    )
    const superAILabelersMatch = useRouteMatch(
        routePaths.appSuperAILabelers(orgUsername, appID)
    )

    const jobSettingsPathMatch = useRouteMatch(
        routePaths.appCustomizeJobParameters(orgUsername, appID)
    )

    const llmSettingsPathMatch = useRouteMatch(
        routePaths.appCustomizeLLMParameters(orgUsername, appID)
    )

    const taskSettingsPathMatch = useRouteMatch(
        routePaths.appCustomizeTaskParameters(orgUsername, appID)
    )

    const appSettingsPathMatch = useRouteMatch(
        routePaths.appSettings(orgUsername, appID)
    )

    const appCollaboratorsPathMatch = useRouteMatch(
        routePaths.appCollaborators(orgUsername, appID)
    )

    const aiModelPredictionPathMatch = useRouteMatch(
        routePaths.appModelList(orgUsername, appID)
    )
    const aiModelTrainingPathMatch = useRouteMatch(
        routePaths.appTrainingList(orgUsername, appID)
    )
    const instanceFetch = useAppInstance(appID)
    const templateFetch = useAppTemplate(appID)
    const actionCls =
        "flex items-center h-full px-0 py-2 rounded text-xs text-center leading-tight text-grape-900 hover:bg-rhino-250 hover:shadow border border-rhino-300 border-solid transition duration-500 ease-in-out no-underline disabled:text-gray-500"
    const rolesFetch = useAssumedRoles(appID)
    const appRoles = useMemo(() => getValue(rolesFetch) ?? [], [rolesFetch])

    const isSysAdmin = useAppState(selectIsSysAdmin)

    const isCanoticCrowd =
        hasValue(instanceFetch) && instanceFetch.value.canoticCrowd
    const unreadJobCount =
        getValue(instanceFetch)?.jobWithFeedbackStats?.totalUnread ?? 0
    const isLabelQualificationAvailable =
        hasValue(templateFetch) && !!templateFetch.value.trainingWorkflow

    const [isMessagesPath] = useAppDetailsPaths()

    const reviewingJobs = useJobIdToReview(appID)
    const jobCount = useJobCount(appID)
    const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)
    const showWelcome = useMemo(() => {
        if (
            !hasValue(instanceFetch) ||
            instanceFetch.value.status === AppInstanceStatus.DISABLED
        ) {
            return false
        }

        if (jobCount?.COMPLETED === 0) {
            return true
        } else if (!hasValue(reviewingJobs) || jobCount === undefined) {
            return false
        } else if (reviewingJobs.value === "nothing_to_review") {
            return false
        }
        return jobCount.COMPLETED === reviewingJobs.value.total
    }, [instanceFetch, jobCount, reviewingJobs])

    const actions = useMemo(() => {
        const items: [string, ReactNode][] = []
        if (
            hasValue(instanceFetch) &&
            !isAppInstanceEnabled(instanceFetch.value)
        ) {
            return Map(items)
        }

        if (hasAppPermission(appRoles, "job:submit")) {
            items.push([
                "upload",
                <Link
                    key="upload"
                    to={routePaths.appAddData(orgUsername, appID, "input")}
                    color="plain"
                    className={`${actionCls} flex-col`}
                >
                    <IconUpload className="mb-2 w-6 h-6" />
                    <span className="whitespace-pre">
                        Upload
                        <br />
                        data
                    </span>
                </Link>
            ])
        }

        if (couldLabel) {
            items.push([
                "label",
                <Link
                    key="label"
                    to={{
                        pathname: routePaths.appDetailLabeling(
                            orgUsername,
                            appID
                        ),
                        state: { isMessagesPath }
                    }}
                    color="plain"
                    className={`${actionCls} flex-col ${
                        canLabel ? "cursor-pointer" : "pointer-events-none"
                    }`}
                >
                    <div
                        className={`flex flex-col items-center ${
                            canLabel ? "" : "text-gray-500"
                        }`}
                    >
                        <IconEdit className={`mb-2`} size="xl" />
                        <span className="whitespace-pre">
                            {appRoles.length === 1 && appRoles[0] === "LABELER"
                                ? "Start processing"
                                : "Process\nmyself"}
                        </span>
                    </div>
                </Link>
            ])
        }

        if (hasAppPermission(appRoles, "job:review")) {
            items.push([
                "review",
                <ReviewUnreviewedButton
                    key="review"
                    orgUsername={orgUsername}
                    appID={appID}
                    sort={filterAndSort?.sort}
                    color="plain"
                    className={`border-rhino-300 flex items-center p-2 rounded text-xs leading-tight ${"flex-col"}`}
                    disabledClassName="text-gray-500"
                >
                    <IconEye className="mb-2" size="xl" />
                    <span className="whitespace-pre">
                        Review
                        <br />
                        results
                    </span>
                </ReviewUnreviewedButton>
            ])
        }
        return Map(items)
    }, [
        instanceFetch,
        appRoles,
        couldLabel,
        orgUsername,
        appID,
        isMessagesPath,
        canLabel,
        filterAndSort
    ])

    const shouldShowManageProjectSubMenu =
        jobSettingsPathMatch !== null ||
        llmSettingsPathMatch !== null ||
        taskSettingsPathMatch !== null ||
        aiModelPredictionPathMatch !== null ||
        aiModelTrainingPathMatch !== null ||
        appSettingsPathMatch !== null ||
        appCollaboratorsPathMatch !== null ||
        workerPathMatch !== null ||
        workerGroupPathMatch !== null ||
        qualifierPathMatch !== null ||
        superAILabelersMatch !== null

    if (!hasValue(rolesFetch)) {
        return null
    }

    return (
        <div className="pt-10">
            {hasValue(instanceFetch) ? (
                <Title
                    component="h1"
                    variant="xl"
                    weight="semibold"
                    className="px-6 pb-2 text-gray-700 break-word relative w-full"
                >
                    <InstanceNameViewEdit
                        appID={appID}
                        instanceName={instanceFetch.value.name}
                        disabled={
                            !isAppInstanceEnabled(instanceFetch.value) ||
                            !hasAppPermission(appRoles, "app:rename")
                        }
                    />
                </Title>
            ) : null}
            <ul className="list-none px-6 pb-2 pt-0 flex">
                {actions.toArray().map(renderAction)}
            </ul>
            <ul
                className="flex flex-col items-stretch list-none p-0 py-4 gap-1"
                data-testid="project-menu"
            >
                {showWelcome && !hasFreemiumPlanExpired && (
                    <li>
                        <MenuItem
                            to={routePaths.appWelcome(orgUsername, appID)}
                            icon={IconWelcome}
                            iconClassName="transform -rotate-45 scale-x-flip"
                            match="exact"
                        >
                            Welcome
                        </MenuItem>
                    </li>
                )}

                {hasAppPermission(appRoles, "job:view") && (
                    <li>
                        <MenuItem
                            to={routePaths.appDetail(orgUsername, appID)}
                            icon={IconDiamond}
                        >
                            Work Queue
                        </MenuItem>
                    </li>
                )}

                {hasAppPermission(appRoles, "ground-truth:view") && (
                    <li>
                        <MenuItem
                            to={routePaths.appCustomizeBaselineData(
                                orgUsername,
                                appID
                            )}
                            icon={IconSettings}
                        >
                            Ground-truth Data
                        </MenuItem>
                    </li>
                )}

                {hasAppPermission(appRoles, "task-design:view") &&
                    hasValue(instanceFetch) &&
                    isAppInstanceEnabled(instanceFetch.value) && (
                        <li>
                            <MenuItem
                                to={routePaths.appCustomizeJobParameters(
                                    orgUsername,
                                    appID
                                )}
                                icon={IconGear}
                                match="never"
                            >
                                Project Settings
                            </MenuItem>
                            {shouldShowManageProjectSubMenu && (
                                <ul className="list-none">
                                    <li>
                                        <MenuItem
                                            size="sm"
                                            to={routePaths.appCustomizeJobParameters(
                                                orgUsername,
                                                appID
                                            )}
                                            match="exact"
                                        >
                                            Job Settings
                                        </MenuItem>
                                    </li>
                                    {isSysAdmin && (
                                        <li>
                                            <MenuItem
                                                size="sm"
                                                to={routePaths.appCustomizeLLMParameters(
                                                    orgUsername,
                                                    appID
                                                )}
                                                match="exact"
                                            >
                                                LLM Settings
                                            </MenuItem>
                                        </li>
                                    )}
                                    <li>
                                        <MenuItem
                                            size="sm"
                                            to={routePaths.appCustomizeTaskParameters(
                                                orgUsername,
                                                appID
                                            )}
                                            match="exact"
                                        >
                                            Task Settings
                                        </MenuItem>
                                    </li>
                                    {hasAppPermission(
                                        appRoles,
                                        "settings:view"
                                    ) && (
                                        <li>
                                            <MenuItem
                                                size="sm"
                                                to={routePaths.appSettings(
                                                    orgUsername,
                                                    appID
                                                )}
                                            >
                                                General Settings
                                            </MenuItem>
                                        </li>
                                    )}
                                    {hasAppPermission(
                                        appRoles,
                                        "collaborators:view"
                                    ) && (
                                        <li>
                                            <MenuItem
                                                size="sm"
                                                to={routePaths.appCollaborators(
                                                    orgUsername,
                                                    appID
                                                )}
                                            >
                                                Collaborators
                                            </MenuItem>
                                        </li>
                                    )}
                                    {hasAppPermission(
                                        appRoles,
                                        "workers:view"
                                    ) ? (
                                        <>
                                            <li>
                                                <MenuItem
                                                    size="sm"
                                                    to={routePaths.appWorkerList(
                                                        orgUsername,
                                                        appID
                                                    )}
                                                    match="exact"
                                                >
                                                    Workers
                                                </MenuItem>
                                            </li>
                                            <li>
                                                <MenuItem
                                                    size="sm"
                                                    to={routePaths.appWorkerGroupList(
                                                        orgUsername,
                                                        appID
                                                    )}
                                                >
                                                    Worker Groups
                                                </MenuItem>
                                            </li>
                                            {isCanoticCrowd &&
                                                hasAppPermission(
                                                    appRoles,
                                                    "crowd:view"
                                                ) && (
                                                    <li>
                                                        <MenuItem
                                                            size="sm"
                                                            to={routePaths.appSuperAILabelers(
                                                                orgUsername,
                                                                appID
                                                            )}
                                                        >
                                                            Super.AI Workers
                                                        </MenuItem>
                                                    </li>
                                                )}
                                            {hasAppPermission(
                                                appRoles,
                                                "training:create"
                                            ) &&
                                                isLabelQualificationAvailable && (
                                                    <li>
                                                        <MenuItem
                                                            size="sm"
                                                            to={routePaths.appQualifiers(
                                                                orgUsername,
                                                                appID
                                                            )}
                                                        >
                                                            Worker Qualification
                                                        </MenuItem>
                                                    </li>
                                                )}
                                        </>
                                    ) : (
                                        hasAppPermission(
                                            appRoles,
                                            "training:create"
                                        ) &&
                                        isLabelQualificationAvailable && (
                                            <li>
                                                <MenuItem
                                                    size="sm"
                                                    to={routePaths.appQualifiers(
                                                        orgUsername,
                                                        appID
                                                    )}
                                                >
                                                    Worker Qualification
                                                </MenuItem>
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </li>
                    )}
                {hasAppPermission(appRoles, "messages:view") && (
                    <li>
                        <MenuItem
                            to={routePaths.appDetailMessages(
                                orgUsername,
                                appID
                            )}
                            icon={MessagesIcon}
                        >
                            Messages
                            {unreadJobCount > 0 && (
                                <div
                                    className="ml-3 text-xs font-semibold align-middle bg-pink-700 px-1 text-white"
                                    style={{ borderRadius: 5 }}
                                    data-testid="unread-message-count"
                                >
                                    {`${
                                        unreadJobCount > 50
                                            ? "50+"
                                            : unreadJobCount
                                    } new`}
                                </div>
                            )}
                        </MenuItem>
                    </li>
                )}
                {hasAppPermission(appRoles, "stats:view") && (
                    <li>
                        <MenuItem
                            to={routePaths.appDetailOverview(
                                orgUsername,
                                appID
                            )}
                            icon={IconOverview}
                        >
                            Statistics
                        </MenuItem>
                    </li>
                )}
            </ul>
        </div>
    )
}
